import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Send, AlertCircle } from 'lucide-react';
import Editor from 'react-simple-code-editor';
import { highlight, languages } from 'prismjs';
import 'prismjs/components/prism-json';
import { toast } from 'react-hot-toast';
import { cn } from '@/lib/utils';
import { useCreateJobFromTemplate, useTemplateVariables } from '@/hooks/useWorkflows';
import type { WorkflowTemplate, WorkflowVariable } from '@/lib/types';

interface Props {
  template: WorkflowTemplate;
  onClose: () => void;
}

const envBadgeStyles: Record<string, string> = {
  production: 'bg-green-500/20 text-green-400 border-green-500/30',
  staging: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  development: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
};

function coerceDefault(value: string | null | undefined, typeName: string): string | number | boolean {
  if (value == null || value === '') return '';
  switch (typeName) {
    case 'integer':
    case 'number': {
      const n = Number(value);
      return Number.isNaN(n) ? '' : n;
    }
    case 'boolean':
      return value === 'true' || value === '1';
    default:
      return value;
  }
}

function parseApiError(err: unknown): string {
  if (!(err instanceof Error)) return 'Unknown error occurred';
  const jsonMatch = err.message.match(/- (\{.+})$/);
  if (jsonMatch) {
    try {
      const parsed = JSON.parse(jsonMatch[1]);
      return parsed?.msg || parsed?.detail?.msg || parsed?.detail || err.message;
    } catch {
      return err.message;
    }
  }
  return err.message;
}

export default function StartJobModal({ template, onClose }: Props) {
  const navigate = useNavigate();
  const createJob = useCreateJobFromTemplate();

  // ESC to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Fetch variables separately if not embedded in template
  const hasEmbeddedVars = Array.isArray(template.variables) && template.variables.length > 0;
  const { data: fetchedVarsData, isLoading: loadingVars, isError: varsError, error: varsErrorObj } = useTemplateVariables(template.id, !hasEmbeddedVars);
  const variables: WorkflowVariable[] = hasEmbeddedVars
    ? template.variables!
    : (fetchedVarsData?.items || []);

  // Form state
  const [inputUrl, setInputUrl] = useState('');
  const [callbackUrl, setCallbackUrl] = useState('');
  const [extraPayloadText, setExtraPayloadText] = useState('');
  const [extraPayloadError, setExtraPayloadError] = useState<string | null>(null);
  const [priority, setPriority] = useState(1);
  const [maxRetries, setMaxRetries] = useState(3);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Variable values keyed by variable name
  const [varValues, setVarValues] = useState<Record<string, string | number | boolean>>(() => {
    const defaults: Record<string, string | number | boolean> = {};
    for (const v of variables) {
      const coerced = coerceDefault(v.default_value, v.type_name);
      if (coerced !== '') {
        defaults[v.name] = coerced;
      } else if (v.type_name === 'boolean' && v.is_required) {
        defaults[v.name] = false;
      }
    }
    return defaults;
  });
  const [varErrors, setVarErrors] = useState<Record<string, string>>({});

  // Re-initialize defaults when variables load asynchronously
  useEffect(() => {
    if (!hasEmbeddedVars && fetchedVarsData?.items?.length) {
      const defaults: Record<string, string | number | boolean> = {};
      for (const v of fetchedVarsData.items) {
        const coerced = coerceDefault(v.default_value, v.type_name);
        if (coerced !== '') {
          defaults[v.name] = coerced;
        } else if (v.type_name === 'boolean' && v.is_required) {
          defaults[v.name] = false;
        }
      }
      setVarValues((prev) => ({ ...defaults, ...prev }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedVarsData, hasEmbeddedVars]);

  const setVarValue = (name: string, value: string | number | boolean) => {
    setVarValues((prev) => ({ ...prev, [name]: value }));
    setVarErrors((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handleExtraPayloadChange = (value: string) => {
    setExtraPayloadText(value);
    setExtraPayloadError(null);
    if (value.trim()) {
      try {
        const parsed = JSON.parse(value);
        if (parsed === null || Array.isArray(parsed) || typeof parsed !== 'object') {
          setExtraPayloadError('Extra payload must be a JSON object');
        }
      } catch {
        setExtraPayloadError('Invalid JSON');
      }
    }
  };

  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    for (const v of variables) {
      if (!v.is_required) continue;
      const val = varValues[v.name];
      if (val === undefined || val === '') {
        errors[v.name] = 'Required';
      } else if (typeof val === 'string' && !val.trim()) {
        errors[v.name] = 'Required';
      }
    }
    setVarErrors(errors);

    if (extraPayloadText.trim()) {
      try {
        const parsed = JSON.parse(extraPayloadText);
        if (parsed === null || Array.isArray(parsed) || typeof parsed !== 'object') {
          setExtraPayloadError('Extra payload must be a JSON object');
          return false;
        }
      } catch {
        setExtraPayloadError('Invalid JSON');
        return false;
      }
    }

    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (createJob.isPending || (!hasEmbeddedVars && (loadingVars || varsError))) return;
    setSubmitError(null);

    if (!validate()) return;

    // Build variables object
    const builtVars: Record<string, unknown> = {};
    for (const v of variables) {
      const val = varValues[v.name];
      if (val === undefined || val === '') continue;
      if (v.type_name === 'boolean') {
        builtVars[v.name] = Boolean(val);
      } else if (v.type_name === 'integer' || v.type_name === 'number') {
        builtVars[v.name] = Number(val);
      } else {
        builtVars[v.name] = val;
      }
    }

    // Parse extra payload
    let extraPayload: Record<string, unknown> | undefined;
    if (extraPayloadText.trim()) {
      try {
        const parsed = JSON.parse(extraPayloadText);
        if (parsed === null || Array.isArray(parsed) || typeof parsed !== 'object') {
          setExtraPayloadError('Extra payload must be a JSON object');
          return;
        }
        extraPayload = parsed;
      } catch {
        setExtraPayloadError('Invalid JSON');
        return;
      }
    }

    try {
      const job = await createJob.mutateAsync({
        template_id: template.id,
        input_url: inputUrl.trim() || undefined,
        variables: Object.keys(builtVars).length > 0 ? builtVars : undefined,
        priority,
        max_retries: maxRetries,
        callback_url: callbackUrl.trim() || undefined,
        extra_payload: extraPayload,
      });
      onClose();
      toast.success(`Job created: ${job.id}`);
      navigate(`/jobs/${job.id}`);
    } catch (err) {
      setSubmitError(parseApiError(err));
    }
  };

  const renderVariableInput = (v: WorkflowVariable) => {
    const value = varValues[v.name] ?? '';
    const error = varErrors[v.name];
    const inputClass = cn(
      'w-full px-3 py-2 rounded-md bg-muted/50 border focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm',
      error ? 'border-red-500' : 'border-border'
    );

    switch (v.type_name) {
      case 'boolean':
        return (
          <div className="flex items-center gap-2 py-2">
            <input
              type="checkbox"
              id={`var-${v.id}`}
              checked={Boolean(value)}
              onChange={(e) => setVarValue(v.name, e.target.checked)}
              className="rounded"
            />
            <label htmlFor={`var-${v.id}`} className="text-sm">
              {v.description || v.name}
              {v.is_required && <span className="text-red-400 ml-1">*</span>}
            </label>
          </div>
        );
      case 'integer':
      case 'number':
        return (
          <input
            id={`var-${v.id}`}
            type="number"
            step={v.type_name === 'integer' ? '1' : 'any'}
            value={value as number | string}
            onChange={(e) => {
              if (e.target.value === '') {
                setVarValue(v.name, '');
                return;
              }
              const next = v.type_name === 'integer'
                ? parseInt(e.target.value, 10)
                : Number(e.target.value);
              setVarValue(v.name, Number.isNaN(next) ? '' : next);
            }}
            className={inputClass}
          />
        );
      case 'url':
        return (
          <input
            id={`var-${v.id}`}
            type="url"
            placeholder="https://..."
            value={value as string}
            onChange={(e) => setVarValue(v.name, e.target.value)}
            className={inputClass}
          />
        );
      default:
        return (
          <input
            id={`var-${v.id}`}
            type="text"
            value={value as string}
            onChange={(e) => setVarValue(v.name, e.target.value)}
            className={inputClass}
          />
        );
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center" role="dialog" aria-modal="true" aria-labelledby="start-job-modal-title">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            if (e.key === ' ') e.preventDefault();
            onClose();
          }
        }}
        role="button"
        tabIndex={0}
        aria-label="Close modal"
      />

      <div className="relative z-10 w-full max-w-3xl max-h-[90vh] m-4 bg-background border border-border rounded-lg shadow-xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <h2 id="start-job-modal-title" className="text-xl font-semibold">Start Job from Template</h2>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-sm font-mono text-muted-foreground">
                {template.type_name}/{template.subtype} v{template.version}
              </span>
              <span
                className={cn(
                  'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border',
                  envBadgeStyles[template.environment] || 'bg-muted text-muted-foreground border-border'
                )}
              >
                {template.environment}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-md hover:bg-muted transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form id="start-job-form" onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Input URL */}
          <div className="space-y-1">
            <label htmlFor="start-job-input-url" className="text-sm font-medium">Input URL</label>
            <input
              id="start-job-input-url"
              type="url"
              placeholder="https://example.com/input.jpg"
              value={inputUrl}
              onChange={(e) => setInputUrl(e.target.value)}
              className="w-full px-3 py-2 rounded-md bg-muted/50 border border-border focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
            />
          </div>

          {/* Variables */}
          {!hasEmbeddedVars && loadingVars && (
            <div className="space-y-1">
              <h3 className="text-sm font-medium">Variables</h3>
              <p className="text-sm text-muted-foreground">Loading variables...</p>
            </div>
          )}
          {!hasEmbeddedVars && varsError && (
            <div className="space-y-1">
              <h3 className="text-sm font-medium">Variables</h3>
              <p className="text-sm text-red-400">
                Failed to load variables: {varsErrorObj instanceof Error ? varsErrorObj.message : 'Unknown error'}
              </p>
            </div>
          )}
          {!loadingVars && !varsError && variables.length === 0 && (
            <div className="space-y-1">
              <h3 className="text-sm font-medium">Variables</h3>
              <p className="text-sm text-muted-foreground">No variables defined for this template.</p>
            </div>
          )}
          {variables.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium">Variables</h3>
              <div className="space-y-3 p-4 rounded-lg bg-muted/30 border border-border">
                {variables.map((v) => (
                  <div key={v.id} className="space-y-1">
                    {v.type_name !== 'boolean' && (
                      <label htmlFor={`var-${v.id}`} className="text-sm">
                        <code className="font-mono bg-muted px-1 rounded text-xs">{v.name}</code>
                        <span className="text-muted-foreground ml-2 text-xs">{v.type_name}</span>
                        {v.is_required && <span className="text-red-400 ml-1">*</span>}
                      </label>
                    )}
                    {renderVariableInput(v)}
                    {v.description && v.type_name !== 'boolean' && (
                      <p className="text-xs text-muted-foreground">{v.description}</p>
                    )}
                    {varErrors[v.name] && (
                      <p className="text-xs text-red-500">{varErrors[v.name]}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Callback URL */}
          <div className="space-y-1">
            <label htmlFor="start-job-callback" className="text-sm font-medium">Callback URL</label>
            <input
              id="start-job-callback"
              type="url"
              placeholder="https://example.com/webhook"
              value={callbackUrl}
              onChange={(e) => setCallbackUrl(e.target.value)}
              className="w-full px-3 py-2 rounded-md bg-muted/50 border border-border focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
            />
          </div>

          {/* Extra Payload */}
          <div className="space-y-1">
            <label htmlFor="start-job-extra-payload" className="text-sm font-medium">Extra Payload (JSON)</label>
            <p className="text-xs text-muted-foreground">
              Merged on top of the generated payload.
            </p>
            <div className={cn(
              'rounded-md border overflow-hidden font-mono text-sm',
              extraPayloadError ? 'border-red-500' : 'border-border'
            )}>
              <Editor
                textareaId="start-job-extra-payload"
                value={extraPayloadText}
                onValueChange={handleExtraPayloadChange}
                highlight={(code) => highlight(code || '', languages.json, 'json')}
                padding={12}
                style={{
                  backgroundColor: 'hsl(var(--background))',
                  minHeight: '80px',
                  fontSize: '13px',
                }}
                placeholder='{"key": "value"}'
              />
            </div>
            {extraPayloadError && (
              <p className="text-xs text-red-500">{extraPayloadError}</p>
            )}
          </div>

          {/* Priority and Max Retries */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label htmlFor="start-job-priority" className="text-sm font-medium">Priority (1–5)</label>
              <input
                id="start-job-priority"
                type="number"
                min={1}
                max={5}
                value={priority}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10) || 1;
                  setPriority(Math.max(1, Math.min(5, val)));
                }}
                className="w-full px-3 py-2 rounded-md bg-muted/50 border border-border focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="start-job-retries" className="text-sm font-medium">Max Retries</label>
              <input
                id="start-job-retries"
                type="number"
                min={0}
                value={maxRetries}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  setMaxRetries(Number.isNaN(val) ? 0 : Math.max(0, val));
                }}
                className="w-full px-3 py-2 rounded-md bg-muted/50 border border-border focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
          </div>

          {/* Error Display */}
          {submitError && (
            <div role="alert" className="p-3 bg-red-500/10 border border-red-500/30 rounded-md">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-400">Failed to create job</p>
                  <p className="text-sm text-red-400/80 mt-1">{submitError}</p>
                </div>
              </div>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-md bg-secondary hover:bg-secondary/80 transition-colors text-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="start-job-form"
            disabled={createJob.isPending || (!hasEmbeddedVars && (loadingVars || varsError))}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="h-4 w-4" />
            {createJob.isPending ? 'Creating...' : 'Create Job'}
          </button>
        </div>
      </div>
    </div>
  );
}
